import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const Disclaimer = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Homepage
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-2 text-[#0047AB]">Disclaimer</h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: January 28, 2025</p>
        
        <div className="prose dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold mb-4">1. Liability for Content</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              The content of our pages has been created with the greatest care. However, we cannot 
              guarantee the accuracy, completeness, or timeliness of the content.
            </p>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed mt-4">
              As a service provider, we are responsible for our own content on these pages in accordance 
              with general laws. However, we are not obligated to monitor transmitted or stored third-party 
              information or to investigate circumstances that indicate illegal activity.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">2. Liability for Links</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Our offer contains links to external websites of third parties, over whose content we have 
              no influence. Therefore, we cannot accept any liability for these external contents. The 
              respective provider or operator of the pages is always responsible for the content of the 
              linked pages.
            </p>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed mt-4">
              The linked pages were checked for possible legal violations at the time of linking. Illegal 
              content was not recognizable at the time of linking. However, permanent content control of the 
              linked pages is not reasonable without concrete evidence of a legal violation.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">3. Exam Preparation</h2>
            <div className="bg-yellow-50 dark:bg-yellow-950/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-900">
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                <strong>Important Notice:</strong> Exam Trainer is an independent learning tool for 
                exam preparation. We are <strong>not an authorized training partner</strong> of PMI®, 
                Scrum.org®, or other certification organizations.
              </p>
            </div>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed mt-4">
              The practice questions and learning materials provided on this platform are for educational 
              purposes only. They are intended to supplement, not replace, official exam preparation.
            </p>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed mt-4">
              We do not guarantee that using this platform will result in passing a certification exam. 
              Success depends on many factors, including your individual preparation and prior knowledge.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">4. Copyright</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              The content and works created by the site operators on these pages are subject to copyright 
              law. Duplication, processing, distribution, and any kind of exploitation beyond the limits 
              of copyright require the written consent of the respective author or creator.
            </p>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed mt-4">
              Downloads and copies of this page are only permitted for private, non-commercial use.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">5. Trademarks</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              All certification names, trademarks, logos, and registered marks referenced on this platform are the property of their respective owners.
            </p>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed mt-4">
              This includes, but is not limited to, certifications and trademarks of organizations such as the Project Management Institute (PMI®), Scrum.org®, and other certification bodies.
            </p>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed mt-4">
              The use of these names and marks is solely for descriptive purposes to identify the certifications users may prepare for and does not imply any affiliation, endorsement, sponsorship, or authorization.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">6. AI Content Disclaimer</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              This platform uses AI-powered features to explain answers. While we strive to provide accurate 
              information, AI-generated content may contain errors. Please verify important information 
              against official sources.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">7. Contact</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              If you have any questions about this disclaimer, please contact us:
            </p>
            <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-lg mt-4">
              <p className="text-slate-700 dark:text-slate-300">
                <strong>Margolytics Ltd.</strong><br />
                128 City Road, London, EC1V 2NX, United Kingdom<br />
                Email: <a href="mailto:info@margolytics.com" className="text-[#0047AB] hover:underline">info@margolytics.com</a><br />
                Phone: +44 20 45 77 05 42
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Disclaimer;
